// Phase Lang-A.8 — Public Language Switcher with "quiet UI" behavior.
//
// Positioning (Phase Lang-A.9 — added May 30):
//   - The component portals its root into `document.body` and uses
//     `position: fixed` so it stays anchored to the top-right of the visible
//     viewport across all scroll/background modes (cover, contain, fixed,
//     scroll, long full-page backgrounds, etc.).
//   - On mobile (<768px): top-right with a 12px gutter + iOS safe-area inset.
//   - On desktop (≥768px): aligned to the right edge of the 430px public
//     invitation shell, NOT the far-right of the browser viewport.
//   - Portaling escapes any ancestor that may have created a containing block
//     (e.g. `transform`, `filter`, `backdrop-filter`) and side-steps iOS
//     Safari `position: fixed` momentum-scroll wobble.
//
// Visual parity with LiquidGlassLanguageSelect (Login/Register):
//   - Full pill: h-9, w-[140px], rounded-md (NOT pill), border-white/40,
//     bg-white/30 + blur(20px) saturate(180%), shadow 0 4px 20px rgba(0,0,0,.08)
//   - Panel:     rounded-md, border-white/40, bg-white/55 + blur(24px) saturate(180%),
//                shadow 0 8px 32px rgba(0,0,0,.12)
//   - Items:     h-8, rounded-sm, focus/hover bg-white/60, selected check mark
//   - Text:      #5a5a5a (AA contrast on translucent surface)
//   - Labels:    full names (Português-PT, Español, English ...)
//   - No flags.
//
// Quiet-luxury behavior unique to the guest page:
//   - 10s auto-compact (trigger morphs to a 42px circular globe)
//   - Scroll-compact past 80px (with TOP_RESTORE_THRESHOLD=40 hysteresis)
//   - Compact globe is intentionally quiet: opacity 0.64, green-tinted glass,
//     lifts to opacity 1 on hover/focus/open
//   - Tap-to-expand from compact mode
//   - Stable full pill while dropdown is open
//   - Post-select preview pill for 3s
//   - ONE-TIME hint pulse (900ms scale+shadow lift) the first time the
//     selector scroll-collapses into compact, per-session, respecting
//     prefers-reduced-motion. Suppressed if user already opened the
//     dropdown or changed languages.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Globe, ChevronDown, Check } from 'lucide-react';
import { findLanguageOption } from '../../lib/i18nInvite';

const AUTO_COMPACT_DELAY = 10_000;
const POST_CHANGE_VISIBLE_DELAY = 3_000;
// Hysteresis: collapse to compact aggressively, restore only when near the top.
// Prevents flutter between states during slow scrolls around the threshold.
const SCROLL_COMPACT_THRESHOLD = 80;
const TOP_RESTORE_THRESHOLD = 40;

const TEXT_COLOR = '#5a5a5a';
const FULL_WIDTH = 140; // px — matches Login's w-[140px]
const TRIGGER_HEIGHT = 36; // px — matches Shadcn h-9
const COMPACT_TAP_SIZE = 42; // px — keeps a usable 40-44 tap zone
const COMPACT_DISC_SIZE = 22; // px — visible glass disc
const COMPACT_GLOBE_OPACITY = 0.7;

const TRIGGER_BASE_STYLE = {
  color: TEXT_COLOR,
  backgroundColor: 'rgba(255, 255, 255, 0.30)',
  border: '1px solid rgba(255, 255, 255, 0.40)',
  WebkitBackdropFilter: 'blur(20px) saturate(180%)',
  backdropFilter: 'blur(20px) saturate(180%)',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
};

// Quieter glass for the compact globe — slightly green-tinted, softer blur,
// taller drop shadow with inset highlight for the crystal edge.
const COMPACT_STYLE = {
  color: TEXT_COLOR,
  backgroundColor: 'rgba(235, 249, 240, 0.18)',
  border: '1px solid rgba(255, 255, 255, 0.32)',
  WebkitBackdropFilter: 'blur(18px) saturate(150%)',
  backdropFilter: 'blur(18px) saturate(150%)',
  boxShadow: '0 8px 24px rgba(18, 48, 40, 0.10), inset 0 1px 0 rgba(255, 255, 255, 0.32)',
};

const PANEL_STYLE = {
  backgroundColor: 'rgba(255, 255, 255, 0.55)',
  border: '1px solid rgba(255, 255, 255, 0.40)',
  WebkitBackdropFilter: 'blur(24px) saturate(180%)',
  backdropFilter: 'blur(24px) saturate(180%)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
};

const HOVER_BG = 'rgba(255, 255, 255, 0.60)';
const SELECTED_BG = 'rgba(255, 255, 255, 0.60)';

// ─── One-time language hint pulse ───────────────────────────────
// Subtle one-shot "this is interactive" cue when the selector first
// scroll-collapses into the compact globe. Once per browser session
// (sessionStorage), suppressed by prefers-reduced-motion, never fires if
// the guest already opened the dropdown or picked a language, never while
// the dropdown is open.
const LANGUAGE_HINT_SESSION_KEY = 'sitoa_language_hint_seen';
const HINT_PULSE_KEYFRAMES_ID = 'sitoa-lang-hint-pulse-keyframes';
const HINT_KEYFRAMES = `
@keyframes sitoaLanguageHintPulse {
  0% {
    transform: scale(1);
    box-shadow: 0 8px 24px rgba(18, 48, 40, 0.10), inset 0 1px 0 rgba(255, 255, 255, 0.32);
  }
  45% {
    transform: scale(1.06);
    box-shadow: 0 10px 30px rgba(18, 48, 40, 0.14), 0 0 0 6px rgba(255, 255, 255, 0.10), inset 0 1px 0 rgba(255, 255, 255, 0.42);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 8px 24px rgba(18, 48, 40, 0.10), inset 0 1px 0 rgba(255, 255, 255, 0.32);
  }
}
@media (prefers-reduced-motion: reduce) {
  .sitoa-lang-hint-pulse { animation: none !important; }
}
`;

function ensureHintKeyframesInjected() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(HINT_PULSE_KEYFRAMES_ID)) return;
  const styleEl = document.createElement('style');
  styleEl.id = HINT_PULSE_KEYFRAMES_ID;
  styleEl.textContent = HINT_KEYFRAMES;
  document.head.appendChild(styleEl);
}

export default function PublicLanguageSwitcher({
  value,
  onValueChange,
  options = [], // [{ code }, ...]
  initialCompact = false, // start in compact mode immediately (skip the 10s wait)
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isCompact, setIsCompact] = useState(initialCompact);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [recentlyChanged, setRecentlyChanged] = useState(false);
  const [shouldPulse, setShouldPulse] = useState(false);
  const rootRef = useRef(null);
  const postChangeTimerRef = useRef(null);
  const pulseTimerRef = useRef(null);
  const hasChangedRef = useRef(false); // ever picked a language this session

  const currentLabel = findLanguageOption(value).name;

  // ─── Inject pulse keyframes once on mount ──────────────────
  useEffect(() => { ensureHintKeyframesInjected(); }, []);

  // ─── One-time language hint pulse trigger ──────────────────
  // Called from the scroll handler the first time we collapse to compact.
  // Reads `hasInteracted`, `hasChangedRef`, `isOpen` at call time via refs
  // so the function identity is stable.
  const isOpenRef = useRef(isOpen);
  const hasInteractedRef = useRef(hasInteracted);
  useEffect(() => { isOpenRef.current = isOpen; }, [isOpen]);
  useEffect(() => { hasInteractedRef.current = hasInteracted; }, [hasInteracted]);

  const maybePulseLanguageHint = useCallback(() => {
    if (typeof window === 'undefined') return;
    try {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      if (window.sessionStorage.getItem(LANGUAGE_HINT_SESSION_KEY) === 'true') return;
    } catch { /* sessionStorage unavailable */ }
    if (hasInteractedRef.current || hasChangedRef.current || isOpenRef.current) return;
    try { window.sessionStorage.setItem(LANGUAGE_HINT_SESSION_KEY, 'true'); } catch { /* */ }
    setShouldPulse(true);
    if (pulseTimerRef.current) window.clearTimeout(pulseTimerRef.current);
    pulseTimerRef.current = window.setTimeout(() => setShouldPulse(false), 1000);
  }, []);

  useEffect(() => () => {
    if (pulseTimerRef.current) window.clearTimeout(pulseTimerRef.current);
  }, []);

  // ─── 10s auto-compact after page load ──────────────────────
  useEffect(() => {
    if (isOpen || hasInteracted || recentlyChanged) return undefined;
    const t = window.setTimeout(() => setIsCompact(true), AUTO_COMPACT_DELAY);
    return () => window.clearTimeout(t);
  }, [isOpen, hasInteracted, recentlyChanged]);

  // ─── Scroll-aware compact (with hysteresis) ────────────────
  // Collapse to compact past SCROLL_COMPACT_THRESHOLD (80px).
  // Restore the full pill only when scrolled back near the top
  // (< TOP_RESTORE_THRESHOLD = 40px). Hysteresis prevents flutter.
  // On the FIRST scroll-triggered collapse we fire the one-time hint pulse.
  const wasCompactRef = useRef(isCompact);
  useEffect(() => { wasCompactRef.current = isCompact; }, [isCompact]);
  useEffect(() => {
    const onScroll = () => {
      if (isOpenRef.current) return;
      const y = window.scrollY || document.documentElement.scrollTop || 0;
      if (y > SCROLL_COMPACT_THRESHOLD) {
        if (!recentlyChanged) {
          const justCollapsed = !wasCompactRef.current;
          setIsCompact(true);
          if (justCollapsed) maybePulseLanguageHint();
        }
      } else if (y < TOP_RESTORE_THRESHOLD) {
        setIsCompact(false);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [recentlyChanged, maybePulseLanguageHint]);

  // ─── Click outside to close dropdown ───────────────────────
  useEffect(() => {
    if (!isOpen) return undefined;
    const onClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('touchstart', onClick);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('touchstart', onClick);
    };
  }, [isOpen]);

  // ─── Cleanup post-change timer on unmount ──────────────────
  useEffect(() => () => {
    if (postChangeTimerRef.current) window.clearTimeout(postChangeTimerRef.current);
  }, []);

  const handleTriggerClick = useCallback(() => {
    setHasInteracted(true);
    if (isCompact) {
      setIsCompact(false);
      setIsOpen(true);
      return;
    }
    setIsOpen((prev) => !prev);
  }, [isCompact]);

  const handleSelect = useCallback((code) => {
    if (code !== value) {
      onValueChange(code);
      hasChangedRef.current = true; // suppress any future hint pulse
    }
    setIsOpen(false);
    setIsCompact(false);
    setShouldPulse(false);
    setRecentlyChanged(true);
    if (postChangeTimerRef.current) window.clearTimeout(postChangeTimerRef.current);
    postChangeTimerRef.current = window.setTimeout(() => {
      setRecentlyChanged(false);
      const y = window.scrollY || document.documentElement.scrollTop || 0;
      if (y > SCROLL_COMPACT_THRESHOLD) setIsCompact(true);
    }, POST_CHANGE_VISIBLE_DELAY);
  }, [value, onValueChange]);

  // While dropdown is open, force full pill + full opacity
  const effectiveCompact = isOpen ? false : isCompact;
  // Compact globe is quiet by default (0.64), lifts to full on hover/focus.
  // Full pill is always at 1.0. Hover-lift is handled by mouse handlers below.
  const [isHovered, setIsHovered] = useState(false);
  const opacity = isOpen ? 1 : (effectiveCompact ? (isHovered ? 1 : 0.64) : 1);

  // Compact: 42×42 transparent tap target containing a 21px visible glass
  // disc. Full: rounded rectangle (h-9 × w-[140px]).
  const triggerBorderRadius = effectiveCompact ? 9999 : 6;
  const triggerHeight = effectiveCompact ? COMPACT_TAP_SIZE : TRIGGER_HEIGHT;
  const triggerWidth = effectiveCompact ? COMPACT_TAP_SIZE : FULL_WIDTH;
  const triggerPadding = effectiveCompact ? 0 : '0 12px';

  // ─── Responsive desktop alignment ──────────────────────────
  // Public invitations are capped at 430px wide on desktop (PublicShell).
  // Pin the switcher to the right edge of THAT shell, not the far-right of
  // the browser viewport. On mobile (<768px) just use a small right gutter.
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 768px)').matches : false
  );
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia('(min-width: 768px)');
    const onChange = (e) => setIsDesktop(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  // Use SSR-safe portal target. Skip rendering until window is available.
  if (typeof document === 'undefined') return null;

  const wrapperStyle = {
    position: 'fixed',
    top: 'calc(env(safe-area-inset-top, 0px) + 12px)',
    right: isDesktop
      ? 'calc(max(0px, (100vw - 430px) / 2) + 12px)'
      : '12px',
    zIndex: 1000,
  };

  return createPortal(
    <div ref={rootRef} style={wrapperStyle} data-testid="public-language-switcher">
      <div className="relative">
      <button
        type="button"
        onClick={handleTriggerClick}
        aria-label="Change language"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        data-testid="language-select"
        data-compact={effectiveCompact ? 'true' : 'false'}
        data-pulse={shouldPulse && effectiveCompact && !isOpen ? 'true' : 'false'}
        className="inline-flex items-center justify-center whitespace-nowrap text-sm focus:outline-none focus-visible:ring-1 focus-visible:ring-white/60"
        style={{
          // In compact mode the BUTTON is a transparent 42×42 tap target.
          // The visible glass disc + globe lives on an inner <span>.
          ...(effectiveCompact
            ? { color: TEXT_COLOR, background: 'transparent', border: 'none', padding: 0 }
            : TRIGGER_BASE_STYLE),
          height: triggerHeight,
          width: triggerWidth,
          borderRadius: triggerBorderRadius,
          ...(effectiveCompact ? {} : { padding: triggerPadding }),
          opacity,
          transition: 'width 280ms cubic-bezier(0.22, 1, 0.36, 1), height 280ms cubic-bezier(0.22, 1, 0.36, 1), border-radius 280ms cubic-bezier(0.22, 1, 0.36, 1), padding 280ms cubic-bezier(0.22, 1, 0.36, 1), opacity 240ms ease, background-color 240ms ease',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onFocus={() => setIsHovered(true)}
        onBlur={() => setIsHovered(false)}
      >
        {effectiveCompact ? (
          <span
            className={`inline-flex items-center justify-center pointer-events-none${shouldPulse && !isOpen ? ' sitoa-lang-hint-pulse' : ''}`}
            style={{
              width: COMPACT_DISC_SIZE,
              height: COMPACT_DISC_SIZE,
              borderRadius: 9999,
              ...COMPACT_STYLE,
              animation: shouldPulse && !isOpen
                ? 'sitoaLanguageHintPulse 900ms cubic-bezier(0.22, 1, 0.36, 1) 1'
                : undefined,
            }}
            aria-hidden="true"
          >
            <Globe
              style={{ color: TEXT_COLOR, opacity: COMPACT_GLOBE_OPACITY, width: 16, height: 16 }}
              aria-hidden="true"
            />
          </span>
        ) : (
          <>
            <Globe className="h-4 w-4 mr-2 shrink-0" style={{ color: TEXT_COLOR }} aria-hidden="true" />
            <span className="flex-1 text-left truncate">{currentLabel}</span>
            <ChevronDown
              className="h-4 w-4 ml-1 opacity-60 shrink-0 transition-transform"
              style={{
                color: TEXT_COLOR,
                transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
              aria-hidden="true"
            />
          </>
        )}
      </button>

      {isOpen && (
        <ul
          role="listbox"
          aria-label="Languages"
          data-testid="language-dropdown"
          className="absolute right-0 mt-1.5 min-w-[140px] rounded-md p-1 z-50"
          style={PANEL_STYLE}
        >
          {options.map((opt) => {
            const code = opt.code;
            const label = findLanguageOption(code).name;
            const selected = code === value;
            return (
              <li
                key={code}
                role="option"
                aria-selected={selected}
                tabIndex={0}
                onClick={() => handleSelect(code)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSelect(code);
                  }
                }}
                data-testid={`language-option-${code}`}
                className="relative flex items-center rounded-sm py-1.5 pl-2 pr-8 cursor-pointer text-sm focus:outline-none"
                style={{
                  color: TEXT_COLOR,
                  backgroundColor: selected ? SELECTED_BG : 'transparent',
                  transition: 'background-color 160ms ease',
                }}
                onMouseEnter={(e) => {
                  if (!selected) e.currentTarget.style.backgroundColor = HOVER_BG;
                }}
                onMouseLeave={(e) => {
                  if (!selected) e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <span className="flex-1">{label}</span>
                {selected && (
                  <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
                    <Check className="h-4 w-4 opacity-80" aria-hidden="true" />
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
      </div>
    </div>,
    document.body
  );
}
